import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Grid, Type } from '../../app/StyleGuide';
import { DangerBadge, SuccessBadge } from '../../components/Badge';
import Breadcrumbs from '../../components/Breadcrumbs';
import { PrimaryButton } from '../../components/Buttons';
import ConfirmationModal from '../../components/ConfirmationModal';
import ErrorMessage from '../../components/ErrorMessage';
import { FormFieldContainer, FormFieldSet, FormGroupContainer, Label, Radio } from '../../components/FormElements';
import { ImageCropUtils } from '../../components/ImageCrop';
import { RaisedContainer } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import SettingsHeader from '../../components/SettingsHeader';
import alertService from '../../services/AlertService';
import orgService from '../../services/orgService';
import uploadService from '../../services/uploadService';
import windowService from '../../services/windowService';
import { handleError } from '../../utils/apiUtils';
import { logger } from '../../utils/logger';
import capitalizeFilter from '../../filters/capitalize-filter';
import WhiteLabel from '../../filters/WhiteLabel';
import { useUser } from '../authentication';
import OrgLogoSelector from './components/OrgLogoSelector';
import { useOrg } from './hooks/useOrg';
import { buildOrgLogoUrl } from './utils/buildOrgLogoUrl';

const SettingsNote = styled.p`
  margin-bottom: ${Grid._6};
  color: ${Type.Color.medium};
`;

const StyledFormGroupContainer = styled(FormGroupContainer)`
  display: flex;
  justify-content: space-between;
`;

interface NoLicenseModalProps {
  handleSubmit: () => void;
  handleDismiss: () => void;
}

const NoLicenseModal = ({ handleSubmit, handleDismiss }: NoLicenseModalProps) => (
  <ConfirmationModal
    buttonActionText="Get Started"
    buttonType="primary"
    title="Get Started"
    prompt={
      <>
        When you purchase and pair a <strong>team</strong> subscription, you can personalize the site and notification
        emails with your custom icon.
      </>
    }
    handleSubmit={handleSubmit}
    handleDismiss={handleDismiss}
  />
);

const ContentContainer = styled(RaisedContainer)`
  margin: ${Grid._10} auto;
  padding: ${Grid._6};
`;

const SettingsFieldSet = styled(FormFieldSet)`
  margin-bottom: ${Grid._7};

  legend {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${Grid._4};
  }
`;

const ChurchAccountStatusSection = ({ isConnected }: { isConnected: boolean }) => {
  const ariaLabel = 'Church account status';

  return (
    <SettingsFieldSet
      name={
        <>
          <span>Lifeway Church Account</span>
          {isConnected ? (
            <SuccessBadge aria-label={ariaLabel}>Connected</SuccessBadge>
          ) : (
            <DangerBadge aria-label={ariaLabel}>Not Connected</DangerBadge>
          )}
        </>
      }
    >
      {isConnected ? (
        <p>
          Your Ministry Grid church is connected to a Lifeway church account.{' '}
          <a href={`${window.config.MYLIFEWAY_BASE_URL}`}>Manage your account profile.</a>
        </p>
      ) : (
        <p>
          Your Ministry Grid church is not connected to a Lifeway church account. Sync your church account to your
          Ministry Grid church to improve your experience.
        </p>
      )}
    </SettingsFieldSet>
  );
};

type OrganizationSettingsContentProps = {
  userId: string;
  orgId: string;
  orgName: string;
  churchAccountId: string | undefined;
  initialOrgLogo: string | undefined;
  initialShowSearch: boolean;
  hasSubscription: boolean;
};

function OrganizationSettingsContent({
  userId,
  orgId,
  orgName,
  churchAccountId,
  initialOrgLogo,
  initialShowSearch,
  hasSubscription,
}: OrganizationSettingsContentProps) {
  const { updateOrg } = useOrg();
  const [modalState, setModalState] = useState<'unlock' | null>(null);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [showSearch, setShowSearch] = useState(initialShowSearch);
  const [isChurchAccountConnected] = useState(Boolean(churchAccountId));
  const capitalizedOrgLabel = capitalizeFilter(WhiteLabel.labels.org);

  const resetLogo = () =>
    orgService
      .resetOrgLogo(orgId, userId)
      .then(() => {
        setLogo(undefined);
        updateOrg(prev => ({ ...prev, logo: undefined }));
        alertService.show(`${capitalizedOrgLabel} Logo Removed`);
      })
      .catch(handleError)
      .finally(() => setIsSaving(false));

  const saveLogo = () => {
    setIsSaving(true);

    if (!logo) return resetLogo();

    ImageCropUtils.convertCroppedImageToBlob(logo)
      .then(blob => uploadService.uploadOrgLogo(orgId, blob))
      .then(() => orgService.setOrgLogo(orgId, userId))
      .then((response: { logo: string }) => {
        setLogo(undefined);
        updateOrg(prev => ({ ...prev, logo: response.logo }));
        alertService.show(`${capitalizedOrgLabel} Logo Saved`);
      })
      .catch(handleError)
      .finally(() => setIsSaving(false));
  };

  const handlePreferenceChange = (preference: boolean) => {
    if (preference === showSearch) return;

    setShowSearch(preference);
    updateOrg(prev => ({ ...prev, showSearch: preference }));

    const orgServiceFn = preference ? orgService.unhideSearch : orgService.hideSearch;
    orgServiceFn(orgId, userId)
      .then(() => alertService.show('Settings Saved'))
      .catch(error => {
        handleError(error);
        setShowSearch(!preference);
        updateOrg(prev => ({ ...prev, showSearch: !preference }));
      });
  };

  return (
    <>
      <ContentContainer data-qa-hook="organizationSettingsView">
        <div className="grid-col-span-4">
          <SettingsHeader title={orgName} subtitle="Organization Settings" icon={<i className="fas fa-cog" />} />
        </div>
        <div className="grid-container grid-col-3 grid-sm-col-6 grid-gap-40 mt-40">
          <div className="grid-col-span-3">
            <ChurchAccountStatusSection isConnected={isChurchAccountConnected} />
            <SettingsFieldSet name="Appearance">
              <OrgLogoSelector
                initialLogo={initialOrgLogo}
                onLogoUpdate={setLogo}
                onUnlockClick={hasSubscription ? null : () => setModalState('unlock')}
              />
              <PrimaryButton
                data-qa-hook="saveChurchLogo"
                onClick={saveLogo}
                disabled={isSaving || logo === undefined}
                operating={isSaving}
              >
                Save {capitalizedOrgLabel} Logo
              </PrimaryButton>
            </SettingsFieldSet>
          </div>
          <div className="grid-col-span-3">
            <SettingsFieldSet name="Advanced Settings">
              <FormFieldContainer>
                <SettingsNote>
                  If your organization wants to prevent those being trained from searching for training on their own,
                  toggling this preference to "no" will remove Training from the menu on both desktop and mobile views.
                  Administrators can still access Training from the Organization page and will have access to all
                  Ministry Grid content when building training or creating Easy Links.
                </SettingsNote>
                <StyledFormGroupContainer>
                  <Label>Show Training in Menu</Label>
                  <div className="controls">
                    <Radio
                      id="showSearch-yes"
                      name="showSearch"
                      onChange={() => handlePreferenceChange(true)}
                      checked={showSearch}
                    />
                    <label htmlFor="showSearch-yes">Yes</label>
                    <Radio
                      id="showSearch-no"
                      name="showSearch"
                      onChange={() => handlePreferenceChange(false)}
                      checked={!showSearch}
                    />
                    <label htmlFor="showSearch-no">No</label>
                  </div>
                </StyledFormGroupContainer>
              </FormFieldContainer>
            </SettingsFieldSet>
          </div>
        </div>
      </ContentContainer>
      {modalState === 'unlock' && (
        <NoLicenseModal handleSubmit={windowService.redirectToPurchase} handleDismiss={() => setModalState(null)} />
      )}
    </>
  );
}

type OrganizationSettingsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; hasSubscription: boolean; showSearch?: boolean };

export default function OrganizationSettings() {
  const { user } = useUser();
  const [data, setData] = useState<OrganizationSettingsState>({ status: 'loading' });
  const { org, isLoading: orgIsLoading, error: orgError } = useOrg();
  const crumbs = [{ name: 'Organization', route: '#/org' }, { name: `Organization Settings` }];

  useEffect(() => {
    if (orgError) {
      console.error('Unable to retrieve org', orgError);
      return setData({ status: 'error' });
    }

    if (orgIsLoading || !org?.id) return;

    orgService
      .hasSubscription(org.id)
      .then((hasSubscription: boolean) =>
        setData({
          status: 'ready',
          hasSubscription,
          showSearch: org.showSearch,
        })
      )
      .catch((error: Error) => {
        logger.error(error);
        setData({ status: 'error' });
      });
  }, [orgIsLoading, orgError, org?.id, org?.showSearch]);

  return (
    <>
      <Breadcrumbs crumbs={crumbs} />
      {data.status === 'loading' ? (
        <LoadingState />
      ) : data.status === 'error' || !user?.userId || !org ? (
        <ErrorMessage>
          A problem occurred showing this page. Please refresh the page to try again. <a href="#/help">Contact Us</a>
        </ErrorMessage>
      ) : (
        <OrganizationSettingsContent
          userId={user.userId}
          orgId={org.id}
          orgName={org.name}
          churchAccountId={org.churchAccountId}
          initialOrgLogo={buildOrgLogoUrl(org.logo)}
          initialShowSearch={data.showSearch || false}
          hasSubscription={data.hasSubscription}
        />
      )}
    </>
  );
}
